'use strict';

/**
 * Car Funding Calculator
 *
 * Usage:
 *
 * var Calculator = new FundingCalculator(
 *   100, // deliveryCosts
 *   2.9, // interest %  (korko)
 *   175, // initialCost (perustamismaksu)
 *   9    // monthlyHandlingCost (käsittelymaksu/kk)
 * );

 * var result = Calculator.calculate({
 *   price         : 17800, // Auton hinta
 *   deposit       : 1500,  // Käsiraha
 *   paymentMonths : 60,    // Maksukuukaudet
 *   lastPayment   : 0      // Viimeinen poikkeava erä
 * });
 *
 */

/**
 * https://www.npmjs.com/package/effective-interest-rate
 */
import effectiveInterestCalculator from 'effective-interest-rate';

/**
 * KSL Annual Interest calculator
 *
 * Math – not even once.
 */
class kslAnnualInterest {
  /**
   * Calculates an effective interest rate with XIRR method.
   *
   * @param {number} principal - The loan principal amount
   * @param {number} duration - The loan duration in months
   * @param {number} monthlyPayment - The calculated monthly payment amount
   * @param {number} [lastPayment] - The optional bigger last payment amount
   * @param {number} [interestGuess] - An initial guess for the annual interest rate, better guess gives better results
   * @param {number} [initialPayment] - The amount that is paid off as handling fees
   *
   * @returns {number} - The approximated annual interest rate
   */
  calculate(
    principal,
    duration,
    monthlyPayment,
    lastPayment = 0,
    interestGuess = 0.059,
    initialPayment = 199
  ) {
    this.principal = principal;
    this.duration = duration;
    this.interestGuess = interestGuess;
    this.lastPayment = lastPayment;
    this.monthlyPayment = monthlyPayment;
    this.initialPayment = initialPayment;

    const payments = this.generatePayments();

    const startDate = this.getStartDate(payments);

    return effectiveInterestCalculator.withSpecifiedPayments(
      this.principal,
      startDate,
      payments,
      this.interestGuess
    );
  }

  /**
   * Generates a payments object in the format required by effective-interest-rate module.
   *
   * @returns {object} - Payment dates and amounts as key-value pairs.
   */
  generatePayments() {
    let payments = {
      '1999-12-01': this.initialPayment
    };
    for (var i = 0; i < this.duration; i++) {
      /**
       * TODO: Payment dates always start at 1999-12-01, which might
       * lead to some inaccuracy. Refactor this to use real dates, for extra accuracy.
       */
      let date = [
        '20', // century
        ('0' + Math.floor(i / 12)).substr(-2), // year
        '-',
        ('0' + ((i % 12) + 1)).substr(-2), // month
        '-01'
      ].join('');

      /**
       * Assign the monthly payment amount, unless a bigger
       * last payment is specified, and it's the last iteration of the loop.
       */
      let paymentAmount =
        i < this.duration - 1 || this.lastPayment <= 0
          ? this.monthlyPayment
          : this.lastPayment;

      payments[date] = paymentAmount;
    }

    return payments;
  }

  /**
   * Find out the loan start date.
   * @param {object} payments - Payment dates and amounts as key-value pairs
   *
   * @return {string} - Loan start date in 'YYYY-MM-DD' format
   */
  getStartDate(payments) {
    return Object.keys(payments)[0];
  }
}

const KAI = new kslAnnualInterest();

const FundingCalculator = {
  /**
   * Construct calculator
   * Sets fixed parameters needed for calculations.
   *
   * @param {Number} deliveryCosts - Sum in Euros
   * @param {Number} interest - Interest percentage
   * @param {Number} initialCost - Sum in Euros
   * @param {Number} monthlyHandlingCost - Sum in Euros
   */
  construct: function(
    deliveryCosts,
    interest,
    initialCost,
    monthlyHandlingCost
  ) {
    // Validate
    if (
      isNaN(parseFloat(deliveryCosts)) ||
      isNaN(parseFloat(interest)) ||
      isNaN(parseFloat(initialCost)) ||
      isNaN(parseFloat(monthlyHandlingCost))
    ) {
      throw new Error('FundingCalculator: Incorrect initialization data.');
    }

    // Käsittelykulut (€)
    this.deliveryCosts = parseFloat(deliveryCosts);

    // Interest (%)
    this.interest = parseFloat(interest) / 100;

    // Perustamismaksu (€)
    this.initialCost = parseFloat(initialCost);

    // Käsittelykulut (€/month)
    this.monthlyHandlingCost = parseFloat(monthlyHandlingCost);
  },

  /**
   * Validate customer parameters
   * @param {Object} params - Parameters for calculations
   * @param {float}  params.price - Price
   * @param {float}  params.deposit - Deposit
   * @param {float}  params.lastPayment - Amount of last installment
   * @param {int}    params.paymentMonths - Length of funding
   *
   * @returns {Object}
   */
  _validateParams: function(params) {
    if (typeof params !== 'object' || params === null) {
      throw new Error('FundingCalculator: Missing calculation data.');
    }

    // Don't allow missing `price` or `paymentMonths`
    if (
      typeof params.price === 'undefined' ||
      typeof params.paymentMonths === 'undefined'
    ) {
      throw new Error('FundingCalculator: Missing calculation data.');
    }

    // Defaults to `0`
    if (typeof params.deposit === 'undefined') {
      params.deposit = 0;
    }

    // Defaults to `0`
    if (typeof params.lastPayment === 'undefined') {
      params.lastPayment = 0;
    }

    // `deposit` cannot be bigger than final price used for calculations
    if (params.deposit > params.price + this.deliveryCosts) {
      params.deposit = Math.max(params.price, 0);
    }

    // `lastPayment` cannot be bigger than `price` without `deposit`
    if (params.lastPayment > params.price - params.deposit) {
      params.lastPayment = Math.max(params.price - params.deposit, 0);
    }

    // Don't allow negative values
    if (
      params.price <= 0 ||
      params.paymentMonths <= 0 ||
      params.deposit < 0 ||
      params.lastPayment < 0
    ) {
      throw new Error('FundingCalculator: Invalid calculation data.');
    }

    return {
      price: parseFloat(params.price),
      paymentMonths: parseInt(params.paymentMonths),
      deposit: parseFloat(params.deposit),
      lastPayment: parseFloat(params.lastPayment)
    };
  },

  /**
   * Calculates the periodic payment for an annuity investment based on constant-amount periodic payments and a constant interest rate.
   * @param {float} rate - Interest rate
   * @param {int} periods - Number of payments to be made
   * @param {float} present - Current value of annuity (elinkorko)
   * @param {float} future - Future value remaining after final payment has been made
   * @param {int} type - Whether payments are due at the end (0) or beginning (1) of each period
   *
   * @returns {float} Periodic payment
   */
  _PMT: function(rate, periods, present, future, type) {
    type = typeof type === 'undefined' ? 0 : type;
    if (rate === 0) {
      return 0 - (present + future) / periods;
    }
    var term = Math.pow(1 + rate, periods);
    return (
      0 -
      (type === 1
        ? ((future * rate) / (term - 1) + (present * rate) / (1 - 1 / term)) /
          (1 + rate)
        : (future * rate) / (term - 1) + (present * rate) / (1 - 1 / term))
    );
  },

  /**
   * Calculates the present value of an annuity investment based on constant-amount periodic payments and a constant interest rate.
   * @param {float} rate - Interest rate
   * @param {int} periods - Number of payments to be made
   * @param {float} payment - Payment amount per period
   * @param {float} future - Future value remaining after the final payment has been made
   * @param {int} type - Whether payments are due at the end (0) or beginning (1) of each period
   *
   * @returns {float}
   */
  _PV(rate, periods, payment, future, type) {
    type = typeof type === 'undefined' ? 0 : type;
    if (rate === 0) {
      return -payment * periods - future;
    } else {
      return (
        (((1 - Math.pow(1 + rate, periods)) / rate) *
          payment *
          (1 + rate * type) -
          future) /
        Math.pow(1 + rate, periods)
      );
    }
  },

  /**
   * Calculates the annual effective interest rate given the nominal rate and number of compounding periods per year
   * @param {float} rate - Interest rate
   * @param {int} period - Number of compunding periods per year
   *
   * @return {float}
   */
  _EFFECT(rate, period) {
    return Math.pow(rate / period + 1, period) - 1;
  },

  /**
   * Calculates the residual payment amount (jäännösarvo)
   * @param {float} interest - Interest rate
   * @param {float} lastPayment - Amount of last payment
   *
   * @returns {float}
   */
  _residualPayment: function(interest, lastPayment) {
    return 0 - this._PV(interest / 12, 1, lastPayment, 0);
  },

  /**
   * Calculates the monthly payment amount (kuukausierä)
   * @param {float} loanAmount - Principal amount (Pääoma)
   * @param {float} interest - Interest rate
   * @param {int} periods - Number of payments to be made (sopimusaika)
   * @param {float} lastPayment - Amount of last payment (Viimeinen isompi erä)
   *
   * @returns {float}
   */
  _monthlyPaymentAmount: function(loanAmount, interest, periods, lastPayment) {
    return this._PMT(
      interest / 12,
      periods - (lastPayment > 0 ? 1 : 0),
      -loanAmount,
      this._residualPayment(interest, lastPayment)
    );
  },

  /**
   * Calculates the monthly payment amount with processing fees
   * @param {float} monthlyPayment - Monthly payment amount
   * @param {float} handlingCost - Monthly handling cost (9€)
   *
   * @returns {float}
   */
  _processingFee: function(monthlyPayment, handlingCost) {
    return monthlyPayment + handlingCost;
  },

  /**
   * Calculates the real loan expenses
   * @param {float} monthlyHandlingCost - Recurring handling fees (Käsittelymaksu)
   * @param {int} periods - Number of payments to be made (Sopimusaika)
   * @param {float} lastPayment - Amount of last payment (Viimeinen isompi erä)
   * @param {float} monthlyPayment - Monthly payment amount (Kuukausierä)
   * @param {float} deposit - Deposit (Käsiraha)
   *
   * @returns {float}
   */
  _creditPrice: function(
    monthlyHandlingCost,
    periods,
    lastPayment,
    monthlyPayment,
    deposit
  ) {
    return (
      monthlyHandlingCost * periods +
      monthlyPayment * (periods - (lastPayment > 0 ? 1 : 0)) +
      lastPayment + deposit
    );
  },

  /**
   * Calculates the monthly payment amount, financed amount, real loan expenses, real loan price and real yearly interest
   * Caveats: Real yearly interest is an approximation
   * @param {object} params - Customer parameters
   *
   * @return {object}
   */
  calculate: function(params) {
    try {
      params = this._validateParams(params);
    } catch (e) {
      return false;
    }

    // Hinta p.l. käsiraha
    var loanAmount = params.price - params.deposit;

    // Rahoitettava osuus
    var financedAmount = params.price + this.initialCost + this.deliveryCosts - params.deposit;

    // Kuukausierä
    var monthlyPaymentAmount = this._monthlyPaymentAmount(
      financedAmount,
      this.interest,
      params.paymentMonths,
      params.lastPayment
    );

    // KSL:n mukainen vuosikorko % yksinkertaisella kaavalla
    // @link https://kulutusluottovertailu.fi/todellinen-vuosikorko/
    var interestGuess =
      this._EFFECT(this.interest, 12) +
      (this.monthlyHandlingCost * 12) / (loanAmount / 2) +
      this.initialCost / loanAmount;

    var KSLInterest = KAI.calculate(
      financedAmount,
      params.paymentMonths,
      monthlyPaymentAmount + this.monthlyHandlingCost,
      params.lastPayment,
      interestGuess,
      this.initialCost
    );

    // KSL:n mukainen luottohinta
    var creditPrice = this._creditPrice(
      this.monthlyHandlingCost,
      params.paymentMonths,
      params.lastPayment,
      monthlyPaymentAmount,
      params.deposit
    );

    // KSL:n mukaiset luottokustannukset
    var loanExpenses = creditPrice - financedAmount - params.deposit + this.initialCost; // Added this.initialCost because https://crasman.atlassian.net/browse/KAYTCONT-446 :shrug:

    var totalLoanCost = creditPrice - params.deposit;

    return {
      creditPrice: creditPrice,
      deposit: params.deposit,
      financedAmount: financedAmount,
      lastPayment: params.lastPayment,
      loanExpenses: loanExpenses,
      monthlyTranche: monthlyPaymentAmount + this.monthlyHandlingCost,
      paymentMonths: params.paymentMonths,
      realYearlyInterest: KSLInterest * 100,
      totalLoanCost: totalLoanCost
    };
  }
};

export default FundingCalculator;
